import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import api, { formatApiError } from "@/lib/api";
import { dateInputToISO, isoToDateInput } from "@/lib/dateFormat";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * Create / edit a Herausforderung (Challenge).
 * Props:
 *   - editing: existing challenge to edit (or null)
 *   - defaultContactIds: pre-fill involved persons
 */
export default function ChallengeDialog({
  open,
  onOpenChange,
  editing = null,
  defaultContactIds = [],
  onSaved,
}) {
  const qc = useQueryClient();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(() => makeInitial(editing, defaultContactIds));

  useEffect(() => {
    if (open) setForm(makeInitial(editing, defaultContactIds));
    // Do NOT depend on `defaultContactIds` here — it may be a fresh array reference on every
    // parent render and would cause an infinite setForm/useEffect loop. Values are captured
    // at the moment the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing]);

  const { data: contactsList = [] } = useQuery({
    queryKey: ["contacts", "all-for-picker"],
    queryFn: async () => (await api.get(`/contacts`, { params: { include_red: "true" } })).data,
    enabled: open,
  });

  const { data: parentOptions = [] } = useQuery({
    queryKey: ["challenges", "all-for-parent"],
    queryFn: async () => (await api.get(`/challenges`)).data,
    enabled: open,
  });

  const setField = (name, value) => setForm((s) => ({ ...s, [name]: value }));

  function toggleContact(cid) {
    setForm((s) => {
      const set = new Set(s.contact_ids || []);
      if (set.has(cid)) set.delete(cid);
      else set.add(cid);
      return { ...s, contact_ids: Array.from(set) };
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.titel?.trim()) {
      toast.error("Bitte gib der Herausforderung einen Titel.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        titel: form.titel.trim(),
        ziel: form.ziel || null,
        status: form.status || "aktiv",
        prioritaet: form.prioritaet || "mittel",
        letzter_schritt: form.letzter_schritt || null,
        naechster_schritt: form.naechster_schritt || null,
        naechster_termin: form.naechster_termin ? dateInputToISO(form.naechster_termin) : null,
        notizen: form.notizen || null,
        parent_challenge_id: form.parent_challenge_id || null,
        contact_ids: form.contact_ids || [],
      };
      if (editing) {
        await api.put(`/challenges/${editing.id}`, payload);
        toast.success("Herausforderung gespeichert");
      } else {
        await api.post(`/challenges`, payload);
        toast.success("Herausforderung gespeichert");
      }
      qc.invalidateQueries({ queryKey: ["challenges"] });
      qc.invalidateQueries({ queryKey: ["contact-dossier"] });
      onSaved?.();
      onOpenChange?.(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  }

  const activeOrRuhend = form.status === "aktiv" || form.status === "ruhend";
  const missingDate = activeOrRuhend && !form.naechster_termin;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="challenge-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">
            {editing ? "Herausforderung bearbeiten" : "Herausforderung erfassen"}
          </DialogTitle>
          <DialogDescription>Titel, Ziel, Status und beteiligte Personen festlegen.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-600">
                Titel <span className="text-rose-500">*</span>
              </Label>
              <Input
                value={form.titel}
                onChange={(e) => setField("titel", e.target.value)}
                required
                className="mt-1"
                data-testid="challenge-field-titel"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-600">Ziel (ein klarer Satz)</Label>
              <Textarea
                value={form.ziel}
                onChange={(e) => setField("ziel", e.target.value)}
                rows={2}
                className="mt-1"
                data-testid="challenge-field-ziel"
              />
            </div>

            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-600">Status</Label>
              <Select value={form.status} onValueChange={(v) => setField("status", v)}>
                <SelectTrigger className="mt-1" data-testid="challenge-field-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="aktiv">Diese Woche aktiv angehen</SelectItem>
                  <SelectItem value="wartet">Wartet auf andere</SelectItem>
                  <SelectItem value="ruhend">Bewusst ruhen</SelectItem>
                  <SelectItem value="abgeschlossen">Abgeschlossen</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-600">Priorität</Label>
              <Select value={form.prioritaet} onValueChange={(v) => setField("prioritaet", v)}>
                <SelectTrigger className="mt-1" data-testid="challenge-field-prioritaet">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hoch">Hoch</SelectItem>
                  <SelectItem value="mittel">Mittel</SelectItem>
                  <SelectItem value="niedrig">Niedrig</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="md:col-span-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-600">Letzter Schritt</Label>
              <Textarea
                value={form.letzter_schritt}
                onChange={(e) => setField("letzter_schritt", e.target.value)}
                rows={2}
                className="mt-1"
                data-testid="challenge-field-letzter_schritt"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-600">Nächster Schritt</Label>
              <Textarea
                value={form.naechster_schritt}
                onChange={(e) => setField("naechster_schritt", e.target.value)}
                rows={2}
                className="mt-1"
                data-testid="challenge-field-naechster_schritt"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-600">
                Nächster Termin / Wiedervorlage
              </Label>
              <Input
                type="date"
                value={form.naechster_termin}
                onChange={(e) => setField("naechster_termin", e.target.value)}
                className="mt-1"
                data-testid="challenge-field-naechster_termin"
              />
              <p className="text-xs text-gray-500 mt-1">Nur ein Datum – eine Uhrzeit ist nicht nötig.</p>
              {missingDate ? (
                <p className="text-xs text-amber-700 mt-1" data-testid="challenge-missing-date-warning">
                  Freundlicher Hinweis: Herausforderungen mit Status „aktiv“ oder „ruhend“ sollten
                  einen nächsten Termin haben. Du kannst trotzdem speichern.
                </p>
              ) : null}
            </div>

            <div className="md:col-span-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-600">Beteiligte Personen</Label>
              <div className="mt-1 grid grid-cols-1 md:grid-cols-2 gap-1 max-h-40 overflow-y-auto border border-gray-200 rounded-md p-2">
                {contactsList.length === 0 ? (
                  <div className="text-xs text-gray-400 italic p-1">Keine Kontakte gefunden.</div>
                ) : (
                  contactsList.map((c) => (
                    <label
                      key={c.id}
                      className="flex items-center gap-2 text-sm text-gray-700 hover:bg-gray-50 px-2 py-1 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={(form.contact_ids || []).includes(c.id)}
                        onChange={() => toggleContact(c.id)}
                        className="accent-black"
                        data-testid={`challenge-field-contact-${c.id}`}
                      />
                      <span>
                        {c.first_name} {c.last_name}
                        {c.company_name ? <span className="text-xs text-gray-500"> – {c.company_name}</span> : null}
                      </span>
                    </label>
                  ))
                )}
              </div>
            </div>

            <div className="md:col-span-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-600">Notizen</Label>
              <Textarea
                value={form.notizen}
                onChange={(e) => setField("notizen", e.target.value)}
                rows={3}
                className="mt-1"
                data-testid="challenge-field-notizen"
              />
            </div>

            <div className="md:col-span-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-600">
                Entstanden aus (optional)
              </Label>
              <Select
                value={form.parent_challenge_id || "__none__"}
                onValueChange={(v) => setField("parent_challenge_id", v === "__none__" ? "" : v)}
              >
                <SelectTrigger className="mt-1" data-testid="challenge-field-parent">
                  <SelectValue placeholder="Keine übergeordnete Herausforderung" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Keine —</SelectItem>
                  {parentOptions
                    .filter((c) => !editing || c.id !== editing.id)
                    .map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.titel}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange?.(false)} data-testid="challenge-cancel-btn">
              Abbrechen
            </Button>
            <Button
              type="submit"
              className="bg-black text-white hover:bg-gray-800"
              disabled={submitting}
              data-testid="challenge-submit-btn"
            >
              {submitting ? "Speichern…" : "Herausforderung speichern"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function toLocalInput(d) {
  // Return YYYY-MM-DD for <input type="date">
  return isoToDateInput(d);
}

function makeInitial(editing, defaultContactIds) {
  if (editing) {
    return {
      titel: editing.titel || "",
      ziel: editing.ziel || "",
      status: editing.status || "aktiv",
      prioritaet: editing.prioritaet || "mittel",
      letzter_schritt: editing.letzter_schritt || "",
      naechster_schritt: editing.naechster_schritt || "",
      naechster_termin: toLocalInput(editing.naechster_termin),
      notizen: editing.notizen || "",
      parent_challenge_id: editing.parent_challenge_id || "",
      contact_ids: (editing.contacts || []).map((l) => l.contact_id),
    };
  }
  return {
    titel: "",
    ziel: "",
    status: "aktiv",
    prioritaet: "mittel",
    letzter_schritt: "",
    naechster_schritt: "",
    naechster_termin: "",
    notizen: "",
    parent_challenge_id: "",
    contact_ids: defaultContactIds || [],
  };
}
